import { Link } from "react-router-dom";
import { galleryImages, heroImages, restaurantInfo, values, cateringUrl } from "../mock";
import SectionHeading from "../components/SectionHeading";
import FAQ from "../components/FAQ";
import SEO, { restaurantSchema, breadcrumbSchema, faqSchema } from "../components/SEO";
import { faqs } from "../data/faqs";
import { ArrowRight, ExternalLink, Wheat, Sprout, Heart, Flame, Utensils, Quote } from "lucide-react";
import { useT, useLang } from "../context/LanguageContext";
import { ABOUT_STRINGS, ABOUT_TIMELINE, SEO_STRINGS } from "../i18n/strings";

const iconMap = { Wheat, Sprout, Heart, Flame };

export default function About() {
  const t = useT();
  const { lang } = useLang();
  const isEs = lang === "es";
  return (
    <>
      <SEO
        title={SEO_STRINGS.about.title.en}
        titleEs={SEO_STRINGS.about.title.es}
        description={SEO_STRINGS.about.description.en}
        descriptionEs={SEO_STRINGS.about.description.es}
        path="/about"
        keywords="Nina's Taqueria story, family Mexican restaurant Los Gatos, authentic Mexican cuisine history, Los Gatos taqueria about"
        keywordsEs="historia Nina's Taqueria, restaurante mexicano familiar Los Gatos, historia cocina mexicana auténtica, taquería Los Gatos"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            restaurantSchema,
            breadcrumbSchema([
              { name: isEs ? "Inicio" : "Home", path: "/" },
              { name: isEs ? "Nosotros" : "About", path: "/about" },
            ]),
            faqSchema(faqs, lang),
          ],
        }}
      />
      <main className="pt-24 bg-cream-50">
      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[2]} alt="Nina's Taqueria — three generations of Mexican family recipes in Los Gatos" className="w-full h-full object-cover ken-burns" />
          <div className="absolute inset-0 bg-gradient-to-r from-granite-dark/85 via-granite-dark/50 to-granite-dark/15" />
          <div className="absolute inset-0 bg-gradient-to-t from-granite-dark/50 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 text-cream">
          <div className="font-hand text-3xl text-brand-light mb-3">{t(ABOUT_STRINGS.heroHand.en, ABOUT_STRINGS.heroHand.es)}</div>
          <h1 className="font-display text-5xl md:text-7xl font-semibold leading-[1.02] max-w-3xl">
            {t(ABOUT_STRINGS.heroTitle.en, ABOUT_STRINGS.heroTitle.es)}
          </h1>
          <p className="mt-6 max-w-2xl text-cream/85 text-lg leading-relaxed">
            {t(ABOUT_STRINGS.heroSub.en, ABOUT_STRINGS.heroSub.es)}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img src={galleryImages[0]} alt="Tacos" className="w-full h-full object-cover" />
            </div>
            <div
              className="sticker absolute -top-6 -right-4 md:-right-8 bg-cream shadow-2xl rounded-2xl p-5 max-w-xs cursor-default"
              style={{ "--rot": "3deg", transform: "rotate(3deg)" }}
              data-testid="sticker-about-quote"
            >
              <Quote className="w-6 h-6 text-brand mb-2" />
              <p className="font-display italic text-granite-dark text-[15px] leading-snug">
                “{t(ABOUT_STRINGS.quoteText.en, ABOUT_STRINGS.quoteText.es)}”
              </p>
              <div className="font-hand text-brand text-lg mt-2">{t(ABOUT_STRINGS.quoteAuthor.en, ABOUT_STRINGS.quoteAuthor.es)}</div>
            </div>
          </div>
          <div>
            <SectionHeading
              align="left"
              eyebrow={t(ABOUT_STRINGS.restEyebrow.en, ABOUT_STRINGS.restEyebrow.es)}
              title={t(ABOUT_STRINGS.restTitle.en, ABOUT_STRINGS.restTitle.es)}
              subtitle={t(ABOUT_STRINGS.restSub.en, ABOUT_STRINGS.restSub.es)}
            />
            <p className="text-granite/70 leading-relaxed mb-5">
              {t(ABOUT_STRINGS.restCopy.en, ABOUT_STRINGS.restCopy.es)}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/menu" className="inline-flex items-center gap-2 bg-brand text-cream px-6 py-3 rounded-full text-sm font-medium hover:bg-brand-dark transition-all">
                {t(ABOUT_STRINGS.restViewMenu.en, ABOUT_STRINGS.restViewMenu.es)} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={cateringUrl("about_page")} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-granite/20 text-granite-dark px-6 py-3 rounded-full text-sm font-medium hover:bg-granite-dark hover:text-cream transition-all">
                {t(ABOUT_STRINGS.restCatering.en, ABOUT_STRINGS.restCatering.es)} <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-5xl mx-auto px-6">
          <SectionHeading
            eyebrow={t(ABOUT_STRINGS.timelineEyebrow.en, ABOUT_STRINGS.timelineEyebrow.es)}
            title={t(ABOUT_STRINGS.timelineTitle.en, ABOUT_STRINGS.timelineTitle.es)}
          />
          <div className="relative">
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-[2px] bg-brand/25 md:-translate-x-[1px]" />
            {ABOUT_TIMELINE.map((step, i) => {
              const year = isEs ? step.year.es : step.year.en;
              const title = isEs ? step.title.es : step.title.en;
              const text = isEs ? step.text.es : step.text.en;
              return (
                <div
                  key={year + i}
                  className="relative mb-12 last:mb-0 md:grid md:grid-cols-2 md:gap-16"
                >
                  <div
                    className={`pl-20 md:pl-0 ${
                      i % 2 === 0
                        ? "md:pr-12 md:text-right"
                        : "md:col-start-2 md:pl-12"
                    }`}
                  >
                    <div className="absolute left-[22px] md:left-1/2 top-2 w-4 h-4 rounded-full bg-brand ring-4 ring-cream md:-translate-x-1/2" />
                    <div className="font-hand text-3xl text-brand leading-none">{year}</div>
                    <h4 className="font-display text-2xl font-semibold mt-1 text-granite-dark">{title}</h4>
                    <p className="text-granite/70 mt-2 leading-relaxed max-w-md md:inline-block">{text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Chefs / Kitchen */}
      <section className="py-24 md:py-32 bg-cream-50">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading
              align="left"
              eyebrow={t(ABOUT_STRINGS.kitchenEyebrow.en, ABOUT_STRINGS.kitchenEyebrow.es)}
              title={t(ABOUT_STRINGS.kitchenTitle.en, ABOUT_STRINGS.kitchenTitle.es)}
              subtitle={t(ABOUT_STRINGS.kitchenSub.en, ABOUT_STRINGS.kitchenSub.es)}
            />
            <p className="text-granite/70 leading-relaxed">
              {t(ABOUT_STRINGS.kitchenCopy.en, ABOUT_STRINGS.kitchenCopy.es)}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-xl mt-8">
              <img src={galleryImages[2]} alt="Kitchen" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-xl">
              <img src={galleryImages[3]} alt="Traditional dishes" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-28 bg-brand-dark text-cream">
        <div className="max-w-6xl mx-auto px-6">
          <SectionHeading
            light
            eyebrow={t(ABOUT_STRINGS.valuesEyebrow.en, ABOUT_STRINGS.valuesEyebrow.es)}
            title={t(ABOUT_STRINGS.valuesTitle.en, ABOUT_STRINGS.valuesTitle.es)}
          />
          <div className="grid md:grid-cols-4 gap-6">
            {values.map((v) => {
              const Icon = iconMap[v.icon] || Utensils;
              return (
                <div key={v.title} className="p-6 rounded-2xl bg-granite-dark/50 border border-cream/10 hover:border-brand-light/50 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center text-granite-dark mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-display text-xl font-semibold">{isEs ? v.titleEs : v.title}</div>
                  <p className="text-cream/75 text-sm leading-relaxed mt-2">{isEs ? v.descriptionEs : v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        light
        eyebrow={t(ABOUT_STRINGS.faqEyebrow.en, ABOUT_STRINGS.faqEyebrow.es)}
        title={t(ABOUT_STRINGS.faqTitle.en, ABOUT_STRINGS.faqTitle.es)}
        subtitle={t(ABOUT_STRINGS.faqSub.en, ABOUT_STRINGS.faqSub.es)}
      />
    </main>
    </>
  );
}
